import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import LineSignShape from "../../../../components/visuals/LineSignShape";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function quadraticLatex(a, b, c) {
  const parts = [];
  parts.push(a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`);
  if (b !== 0) {
    const bPart = Math.abs(b) === 1 ? "x" : `${Math.abs(b)}x`;
    parts.push(b > 0 ? `+ ${bPart}` : `- ${bPart}`);
  }
  if (c !== 0) {
    parts.push(c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`);
  }
  return parts.join(" ");
}

function lineLatex(a, b) {
  if (a === 0) return `${b}`;
  const aPart = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  if (b === 0) return aPart;
  return b > 0 ? `${aPart} + ${b}` : `${aPart} - ${Math.abs(b)}`;
}

function buildTaskData() {
  const x0 = randInt(-4, 4);
  const y0 = randInt(-4, 4);
  const a = randInt(-2, 2, [0]);
  const b = -2 * a * x0;
  const c = a * x0 * x0 + y0;

  const dA = 2 * a;
  const dB = b;

  const leftSign = a > 0 ? "-" : "+";
  const rightSign = a > 0 ? "+" : "-";

  const askIncreasing = Math.random() < 0.5;
  const askLabel = askIncreasing ? "rosnaca" : "malejaca";

  // Maksymalny przedzial monotonicznosci: domkniety przy x0.
  const expected = askIncreasing
    ? (a > 0
      ? { left: x0, right: Infinity, leftClosed: true, rightClosed: false }
      : { left: -Infinity, right: x0, leftClosed: false, rightClosed: true })
    : (a > 0
      ? { left: -Infinity, right: x0, leftClosed: false, rightClosed: true }
      : { left: x0, right: Infinity, leftClosed: true, rightClosed: false });

  const expectedLatex = `${expected.leftClosed ? "[" : "("}${expected.left === -Infinity ? "-\\infty" : expected.left};\\,${expected.right === Infinity ? "\\infty" : expected.right}${expected.rightClosed ? "]" : ")"}`;

  return {
    a,
    b,
    c,
    x0,
    dA,
    dB,
    leftSign,
    rightSign,
    askIncreasing,
    askLabel,
    expected,
    expectedLatex,
    functionLatex: quadraticLatex(a, b, c),
    derivativeLatex: lineLatex(dA, dB),
  };
}

function eq(a, b, tol = 1e-9) {
  if (a === b) return true; // obsluga m.in. Infinity i -Infinity
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= tol;
}

function sameInterval(user, expected) {
  if (!user || !expected) return false;
  return eq(user.left, expected.left)
    && eq(user.right, expected.right)
    && user.leftClosed === expected.leftClosed
    && user.rightClosed === expected.rightClosed;
}

export function generateTask() {
  const data = buildTaskData();
  return {
    type: "QuadraticMonotonicityTask",
    prompt: `Wskaz maksymalny przedzial, na ktorym funkcja f(x) = ${data.functionLatex} jest ${data.askLabel}.`,
    answer: data.expectedLatex,
    data,
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wskaz maksymalny przedzial, na ktorym funkcja jest <strong>{data.askLabel}</strong>:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>Zapisz odpowiedz jako przedzial.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const val = userAnswer?.interval ?? "";
  return (
    <div className="space-y-2">
      <IntervalInput
        value={val}
        onChange={(next) => onChange({ ...(userAnswer || {}), interval: next })}
        disabled={disabled}
        asString
        previewWithX
        prefixAbove
        placeholder="np. [2; +inf)"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer?.interval === "string"
    ? userAnswer.interval
    : userAnswer?.interval?.raw || "";
  if (!raw.trim()) return false;

  const parsed = parseIntervalInput(raw);
  if (!parsed || parsed.kind !== "intervals" || parsed.intervals.length !== 1) return false;
  return sameInterval(parsed.intervals[0], data.expected);
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const ua = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  const raw = typeof ua?.interval === "string" ? ua.interval : ua?.interval?.raw || "";
  if (!raw.trim()) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={intervalToLatex(raw, { withX: true })} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x\\in ${data.expectedLatex}`} />;
}

export function renderExplanation({ data }) {
  const growthLeft = data.leftSign === "+" ? "rosnie" : "maleje";
  const growthRight = data.rightSign === "+" ? "rosnie" : "maleje";
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Liczymy pochodna funkcji.</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />

      <p><strong>Krok 2:</strong> Wyznaczamy miejsce zerowe pochodnej.</p>
      <BlockMath math={`${data.derivativeLatex} = 0`} />
      <BlockMath math={`x_0 = ${data.x0}`} />

      <p><strong>Krok 3:</strong> Odczytujemy znak pochodnej po lewej i prawej stronie punktu <InlineMath math={`x_0=${data.x0}`} />.</p>
      <div style={{ maxWidth: 350, margin: "20px auto" }}>
        <LineSignShape
          increasing={data.dA > 0}
          zeroLabel={String(data.x0)}
          leftSign={data.leftSign}
          rightSign={data.rightSign}
          width={350}
        />
      </div>
      <p>
        Dla <InlineMath math={`x < ${data.x0}`} /> pochodna ma znak <strong>{data.leftSign}</strong>,
        a dla <InlineMath math={`x > ${data.x0}`} /> ma znak <strong>{data.rightSign}</strong>.
      </p>
      <p>
        Zatem funkcja po lewej stronie <strong>{growthLeft}</strong>, a po prawej stronie <strong>{growthRight}</strong>.
      </p>

      <p><strong>Krok 4:</strong> Wybieramy szukany przedzial.</p>
      <p>Szukamy przedzialu, na ktorym funkcja jest <strong>{data.askLabel}</strong>.</p>
      <BlockMath math={`x\\in ${data.expectedLatex}`} />
    </div>
  );
}
